<template>
  <div>
    <h2>Projekte</h2>
    <p>
      Willkommen zu Ihrem Einsparpotential.
      Diese Seite zeigt die Ergebnisse aller berechneten Maßnahmen und Maßnahmengruppen.
      Das Ausschließen von Maßnahmen und Maßnahmengruppen ist auf der Seite
      <router-link
        v-if="projectId"
        :to="{ name: 'ProjectDashboard', params: { projectId: String(projectId) } }"
      >
        <strong>Maßnahmen</strong>
      </router-link>
      möglich.
    </p>

    <p>
      Für jede einzelne Maßnahme werden die Energiekosteneinsparungen, die
      Investitionskosten sowie die Einsparungen an Energieträgern angegeben.
      Auf die Maßnahmen und auf deren Parameter kann über das Stift-Symbol
      (Maßnahme bearbeiten) zugegriffen werden. Maßnahmengruppen werden in der Analyse
      als eine große Maßnahme zusammengefasst. Maßnahmengruppen können aufgeklappt
      und im Detail betrachtet werden, um die Einzelmaßnahmen zu vergleichen.
      Über alle ausgewählten Maßnahmen werden darüber hinaus die Summen gebildet.
      Mit der Schaltfläche oben rechts können die Ergebnisse wahlweise als absolute Werte
      oder als relative (prozentuale) Einsparungen angezeigt werden.
      Um die Ergebnisse der Analyse für die weitere Verwendung und Bearbeitung zu erhalten,
      können Bericht (alle Maßnahmen u. Gruppen), Kurzberichte (einzelne Maßnahmen oder Gruppen)
      und Excel Tabellen (Analyse Tabelle) erzeugt und heruntergeladen werden.
    </p>

    <dl>
      <dt><strong>Hinweise:</strong></dt>
      <dd>
        Für die Einsparung der überschlägigen Maßnahmen ist eine geringe Genauigkeit
        der Berechnung zu berücksichtigen, da aufgrund der geringen Anzahl an
        Parametern nur eine Genauigkeit von +- 15% möglich ist. Deshalb wird bei
        ungewöhnlich hohen Einsparungen empfohlen, diese Maßnahmen mit der
        ausführlichen Methode gegenzurechnen.
      </dd>
      <dd>
        Bei den absoluten Einsparpotentialen ist zu beachten, dass die Referenzanlagen
        und die geplanten Anlagen der Maßnahmen theoretisch berechnet werden. Die somit
        theoretisch berechneten absoluten Einsparungen entsprechen nicht den tatsächlichen
        Einsparungen in der Praxis. Für eine präzisere Aussage über die Einsparung
        können die relativen Einsparungen in Bezug auf gemessene Verbrauchswerte bezogen werden.
      </dd>
    </dl>

    <p>Folgende Funktionen sind hier vorhanden:</p>
    <ul>
      <li>Übersicht über alle Einsparungen.</li>
      <li>Ergebnisse zwischen absoluten und relativen Werten umschalten.</li>
      <li>Fehler- und Warnungshinweise bei fehlenden Eingaben beachten.</li>
      <li>Erzeuger-Details je Maßnahme aufklappen.</li>
      <li>Berichte herunterladen für alle Maßnahmen, Maßnahmengruppen und Einzelmaßnahmen.</li>
      <li>Excel Tabelle herunterladen.</li>
    </ul>

    <v-expansion-panels accordion class="mt-5">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Zusätzliche Informationen</div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <h3>Absolute und relative Werte</h3>
          <p>
            Über die Schaltfläche oben rechts wechseln Sie zwischen der Darstellung als
            Absolutwerte (z. B. €/a, kWh, kg/a) und als relative, prozentuale Einsparungen.
            Die Umschaltung gilt für alle Spalten der Ergebnistabelle.
          </p>

          <h3>Ergebnisspalten</h3>
          <p>
            Die Ergebnistabelle enthält je Maßnahme die Spalten Energiekosten, Einspeisung
            (nur wenn eine Einspeisung vorhanden ist), Wartungskosten, CO₂ und Investition.
            Die Energieeinsparungen sind zusätzlich nach Kategorien gruppiert:
          </p>
          <ul>
            <li>Endenergien</li>
            <li>Nutzenergien</li>
            <li>Eigenverbrauch Erneuerbare Energie</li>
            <li>Einspeisung Erneuerbare Energie</li>
          </ul>

          <h3>Fehler und Warnungen</h3>
          <p>
            Fehlen für die Berechnung benötigte Eingaben (z. B. Energiepreise), erscheinen
            oberhalb der Tabelle entsprechende Fehler- oder Warnungshinweise; an der betroffenen
            Maßnahme wird zusätzlich ein Warnsymbol angezeigt. Ergänzen Sie die fehlenden Werte
            in den Projektdetails, damit die Analyse vollständig berechnet werden kann.
          </p>

          <h3>Erzeuger</h3>
          <p>
            Maßnahmen mit verknüpften Erzeugern lassen sich über das Pfeil-Symbol aufklappen, um
            die einzelnen Erzeuger anzuzeigen. Die Farbpunkte der Erzeuger werden in der Legende
            (Warm- bzw. Kälteerzeuger) unterhalb der Tabelle erläutert.
          </p>

          <h3>Berichte &amp; Export</h3>
          <p>
            Für die weitere Verwendung stehen mehrere Exporte bereit: der Word-Bericht (alle
            Maßnahmen und Gruppen), Kurzberichte als Word-Export je Gruppe bzw. je Maßnahme sowie
            alle Kurzberichte gesammelt als Zip-Datei und die Analyse-Tabelle als Excel-Datei.
          </p>

          <h3>Berechnung</h3>
          <p>Folgende Annahmen wurden für das Einsparpotential angenommen:</p>
          <ul>
            <li>
              Für die Kälteenergieeinsparung wird ein COP (Kälteleistungszahl) von 4 verwendet.
            </li>
            <li>
              Die Investitionskosten berechnen sich aus den Demontagekosten und den Kosten der Neuanlage.
            </li>
            <li>
              Bei der Wärmeenergieeinsparung wird für die
              Verbrennung und die Fernwärme ein Wirkungsgrad von 0,9 bzw. 1 verwendet.
            </li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useProjectStore } from '@/stores/projectStore';

// Get projectId from the store; guard against null
const store = useProjectStore();
const projectId = computed(() => store.currentProject?.id ?? null);

</script>

<style scoped>
</style>
